/*
 * *****************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Wed, Jul 21st 2021, 10:02:41 pm
 *
 * *****************************************************
 */

import { useCallback } from 'react';

import { TextStyle, Form, Button, FormLayout, Link } from '@shopify/polaris';
import { FORM_ERROR } from 'final-form';
import { Form as FinalForm, Field } from 'react-final-form';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';

import { CONTACT_US_LINK, HELP_CENTER_LINK } from 'constants/links';
import { CREATE_WORKSPACE_RECAPTCHA_ACTION } from 'constants/recaptcha-actions';
import { WORKSPACE_NAME_MAX_LENGTH } from 'constants/workspace';
import { SELECT_WORKSPACE } from 'features/auth/routes/paths';
import FormErrorBanner from 'features/common/views/components/FormErrorBanner';
import { useCreateWorkspaceMutation } from 'states/services/auth/hooks';
import { SubmissionResponse, convertApiErrorsToFinalFormSubmitErrors } from 'utils/form/convert';
import { composeValidators, required, maxLength } from 'utils/form/validation';
import { useGoogleReCaptchaV3 } from 'utils/hooks';

import BackButton from '../../components/BackButton';
import TextFieldAdapter from '../../components/TextFieldAdapter';
import AuthLayout from '../../layouts/AuthLayout';
import CreateWorkspaceLoading from './CreateWorkspaceLoading';
import { H1, ContentContainer } from './styles';

type Props = {
  location: {
    pathname: string;
    state: {
      referrer?: string | null;
    };
  };
};
/**
 * Create workspace page
 */
const CreateWorkspace = ({ location }: Props): JSX.Element => {
  const history = useHistory();
  /**
   * [apiTrigger, apiResult] of RTK Mutation
   */
  const [createWorkspace] = useCreateWorkspaceMutation();
  const { isReady: isReCaptchaReady, execute: executeReCaptcha } = useGoogleReCaptchaV3();
  const isReferrerSelectWorkspacePage = location.state.referrer === SELECT_WORKSPACE;

  const handleBackButtonClick = useCallback(() => {
    const { state } = location;
    const { referrer } = state;
    history.push(referrer!);
  }, [history, location]);

  const handleFormSubmit = useCallback(
    async values => {
      /**
       * Get the captcha token
       */
      if (!isReCaptchaReady) {
        return {
          [FORM_ERROR]: 'Execute recaptcha not yet available. Please try again!'
        };
      }

      const captchaToken = await executeReCaptcha(CREATE_WORKSPACE_RECAPTCHA_ACTION);

      /**
       * Prepare the request data
       */
      const data = {
        name: values.name.trim(),
        captchaToken
      };

      /**
       * Make the request
       */
      const res = (await createWorkspace(data)) as SubmissionResponse;

      if (res.error) {
        /**
         * Return the submission errors for displaying (if any)
         */
        return convertApiErrorsToFinalFormSubmitErrors(res.error);
      }

      /**
       * Redirect user to workspace domain when successfully signing in to workspace
       */
      setTimeout(() => {
        window.location.replace(
          `${process.env.HTTPS ? 'https' : 'http'}://${process.env.REACT_APP_WORKSPACE_DOMAIN}`
        );
      }, 500);

      return undefined;
    },
    [isReCaptchaReady, executeReCaptcha, createWorkspace]
  );

  return (
    <AuthLayout>
      <Helmet>
        <title>Create workspace | WiseIO</title>
        <meta name="description" content="Create workspace" />
        <meta property="og:url" content={window.location.href} />
        <meta name="robots" content="noindex" />
      </Helmet>
      {isReferrerSelectWorkspacePage && <BackButton onClick={handleBackButtonClick} />}
      <header>
        <H1>Create workspace</H1>
        <TextStyle variation="subdued">
          A workspace is where you host your projects and <br />
          collaborate with teammates.
        </TextStyle>
      </header>

      <FinalForm
        onSubmit={handleFormSubmit}
        render={({
          handleSubmit,
          submitting,
          submitSucceeded,
          hasSubmitErrors,
          submitErrors,
          values
        }) => {
          return submitting || submitSucceeded ? (
            <CreateWorkspaceLoading />
          ) : (
            <>
              <ContentContainer>
                {hasSubmitErrors && <FormErrorBanner data={submitErrors} />}
                <Form onSubmit={handleSubmit}>
                  <FormLayout>
                    <Field
                      name="name"
                      component={TextFieldAdapter}
                      label="Workspace name"
                      disabled={submitting}
                      placeholder="e.g. Awesome Team"
                      helpText="Choose a name that is easily recognized by your team."
                      requiredIndicator
                      validate={composeValidators(
                        required('Workspace name'),
                        maxLength('Workspace name', WORKSPACE_NAME_MAX_LENGTH)
                      )}
                    />
                    <Button submit primary disabled={!values.name}>
                      Create Workspace
                    </Button>
                  </FormLayout>
                </Form>
              </ContentContainer>
              <footer>
                <TextStyle variation="subdued">
                  Having a problem? Visit our{' '}
                  <Link removeUnderline url={HELP_CENTER_LINK}>
                    Help Center
                  </Link>{' '}
                  or{' '}
                  <Link removeUnderline url={CONTACT_US_LINK}>
                    Contact us
                  </Link>
                </TextStyle>
              </footer>
            </>
          );
        }}
      />
    </AuthLayout>
  );
};

export default CreateWorkspace;
